import type {
  FormFieldUICopy,
  ModalUICopy,
  ScreenUICopy,
} from '@polyai/ui/uiCopy/types';

import type { CardColumnUICopy } from 'uiCopy/types';

const SCREEN_UI_COPY: ScreenUICopy<string, string> = {
  pageTitle: 'SMS',
  pageDescription:
    'Set multiple SMS templates that can be sent to your callers.',
} as const;

const TWILIO_MODAL_UI_COPY: ModalUICopy<
  string,
  (renderLink: (content: string) => React.ReactNode) => React.ReactNode
> = {
  title: 'Connect your agent to Twilio',
  description: (renderLink: (content: string) => React.ReactNode) => (
    <>
      Log into your Twilio account and find the section &quot;Account info&quot;
      on this {renderLink('page')}
    </>
  ),
  button: {
    confirm: 'Done',
    cancel: 'Cancel',
  },
} as const;

const TWILIO_FORM_UI_COPY = {
  accountSID: {
    label: 'Account SID',
    placeholder: 'Account SID',
    requiredError: 'Account SID is required',
    helpText: {
      default:
        'A 34 character alphanumeric identifier that begins with the letters "AC"',
      missingPrefix: 'Phone number is missing the "+" prefix',
      invalidFormat: 'Only phone numbers are permitted in this field',
    },
  } satisfies Omit<FormFieldUICopy, 'helpText'> & {
    helpText: {
      default: string;
      missingPrefix: string;
      invalidFormat: string;
    };
  },
  authToken: {
    label: 'Auth token',
    placeholder: 'Auth token',
    requiredError: 'Auth token is required',
    helpText:
      "The authentication token verifies the identity when using Twilio's API",
  } satisfies FormFieldUICopy,
  phoneNumber: {
    label: 'Phone number or Messaging Service ID',
    placeholder:
      'Phone number or Messaging Service ID (e.g., MG1234567890abcdef)',
    helpText:
      'Phone numbers on your Twilio account or Messaging Service IDs (MG format) that you would like to be used for sending SMS',
  } satisfies FormFieldUICopy,
} as const;

const CARD_UI_COPY = {
  smsText: {
    title: 'SMS text',
  } satisfies CardColumnUICopy,
  usageCount: {
    title: 'Usage count',
    titleTooltip:
      'The number of times an SMS template has been referenced in a Topic, Function or Flow Step.',
  } satisfies CardColumnUICopy,
  popupMenu: {
    edit: 'Edit',
    duplicate: 'Duplicate',
    delete: 'Delete',
    view: 'View',
  },
  modal: {
    delete: {
      title: 'Delete this SMS template?',
      description: usageCount => {
        const constantCopy =
          'Deleting this SMS template could break functionality where used within the Knowledge Base, Flows and Functions.';
        if (usageCount === undefined) {
          return constantCopy;
        }
        return [
          constantCopy,
          `It is being used in ${usageCount} place${
            usageCount === 1 ? '' : 's'
          } in the project.`,
        ].join('\n');
      },
    } satisfies Omit<
      ModalUICopy<string, (usageCount?: number) => string>,
      'button'
    >,
  },
} as const;

export const SMS_TEMPLATES = {
  ...SCREEN_UI_COPY,
  button: {
    add: 'SMS',
    connectTwilio: 'Connect Twilio account',
  },
  empty: {
    title:
      'You’ll need to connect your Twilio phone number using a SIP address before handing off agent calls.',
    noSmsText: {
      title: "You don't have any SMS templates yet",
      description:
        'Press the "Add SMS" button to create your first SMS template',
    },
    noConnectionText: {
      title:
        'You’ll need to connect your Twilio phone number using a SIP address before handing off agent calls.',
    },
  },
  modal: {
    twilio: {
      ...TWILIO_MODAL_UI_COPY,
      firstTimeConnect: {
        button: {
          confirm: 'Connect',
          cancel: 'Cancel',
        },
      },
      form: TWILIO_FORM_UI_COPY,
    },
  },
  card: CARD_UI_COPY,
} as const;
